import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AdminHours } from '../../API/ClientAPIHelpers/adminHours';
import { ErrorProperties } from '../../API/identityAPIProperties';
import API from '../../API/Main/API';
import { resetState } from '../../resetState';
import {
	CreateUserSessionAllocationProperties,
	UpdateUserSessionAllocationProperties,
	UserSessionAllocationMonthlyDetailsProperties,
	UserSessionAllocationPaginationProperties,
	UserSessionAllocationProperties,
} from '../../API/userAPIProperties';
import { GetRemainingHoursProperties } from '../../API/ClientAPIHelpers/userSessionAllocationProperties';

export const getUserSessionAllocations = createAsyncThunk(
	'user/session-allocations/get-all',
	async (data: UserSessionAllocationPaginationProperties, thunkAPI) => {
		const response = await API.UserAPI.UserSessionAllocation.getAllocations(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as UserSessionAllocationProperties[];
	},
);

export const getUserSessionAllocation = createAsyncThunk(
	'user/session-allocations/get',
	async (id: string, thunkAPI) => {
		const response = await API.UserAPI.UserSessionAllocation.getAllocation(id);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as UserSessionAllocationProperties;
	},
);

export const createUserSessionAllocation = createAsyncThunk(
	'user/session-allocations/create',
	async (data: CreateUserSessionAllocationProperties, thunkAPI) => {
		const response = await API.UserAPI.UserSessionAllocation.createAllocation(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as UserSessionAllocationProperties;
	},
);

export const updateUserSessionAllocation = createAsyncThunk(
	'user/session-allocations/update',
	async (data: UpdateUserSessionAllocationProperties, thunkAPI) => {
		const response = await API.UserAPI.UserSessionAllocation.updateAllocation(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as UserSessionAllocationProperties;
	},
);

export const deleteUserSessionAllocation = createAsyncThunk(
	'user/session-allocations/delete',
	async (id: string, thunkAPI) => {
		const response = await API.UserAPI.UserSessionAllocation.deleteAllocation(id);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

export const getUserActiveAllocations = createAsyncThunk(
	'user/session-allocations/get-active',
	async (userId: string, thunkAPI) => {
		const response = await API.UserAPI.UserSessionAllocation.getUserActiveAllocations(userId);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as UserSessionAllocationProperties[];
	},
);

export const checkUserHasAllocation = createAsyncThunk(
	'user/session-allocations/check-has-allocation',
	async (userId: string, thunkAPI) => {
		const response = await API.UserAPI.UserSessionAllocation.checkUserHasAllocation(userId);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as boolean;
	},
);

export const getUserRemainingHours = createAsyncThunk(
	'user/session-allocations/get-remaining-hours',
	async (data: GetRemainingHoursProperties, thunkAPI) => {
		const response = await API.ClientAPI.UserSessionAllocation.getUserRemainingHours(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as UserSessionAllocationMonthlyDetailsProperties;
	},
);

export const getAdminHoursUsage = createAsyncThunk(
	'user/admin-hours/usage',
	async ({ userId, date }: { date: string; userId: string }, thunkAPI) => {
		try {
			return await AdminHours.getUsage(userId, date);
		} catch (error) {
			return thunkAPI.rejectWithValue(error);
		}
	},
);

interface UserSessionAllocationInitialStateProperties {
	adminHoursUsage: UserSessionAllocationMonthlyDetailsProperties | null;
	adminHoursUsageRequestId?: string;
	error: ErrorProperties;
	isDeleting: boolean;
	loading: boolean;
	userActiveAllocations: UserSessionAllocationProperties[];
	userHasAllocation: boolean;
	userRemainingHours: UserSessionAllocationMonthlyDetailsProperties | null;
	userSessionAllocations: UserSessionAllocationProperties[];
}

const initialUserSessionAllocationState: UserSessionAllocationInitialStateProperties = {
	adminHoursUsage: null,
	loading: false,
	error: {
		status: 0,
		title: '',
	},
	isDeleting: false,
	userSessionAllocations: [],
	userActiveAllocations: [],
	userHasAllocation: false,
	userRemainingHours: null,
};

const userSessionAllocationSlice = createSlice({
	name: 'userSessionAllocation',
	initialState: initialUserSessionAllocationState,
	reducers: {
		clearDiscretionarySessionState(state) {
			return {
				...initialUserSessionAllocationState,
				// Clinical-dialog cleanup must not clear the calendar's Admin Hours allocation.
				adminHoursUsage: state.adminHoursUsage,
				adminHoursUsageRequestId: state.adminHoursUsageRequestId,
			};
		},
	},
	extraReducers: (builder) => {
		builder.addCase(getAdminHoursUsage.pending, (state, action) => {
			state.adminHoursUsageRequestId = action.meta.requestId;
			state.adminHoursUsage = null;
		});
		builder.addCase(getAdminHoursUsage.fulfilled, (state, action) => {
			if (state.adminHoursUsageRequestId === action.meta.requestId) state.adminHoursUsage = action.payload;
		});
		builder.addCase(getAdminHoursUsage.rejected, (state, action) => {
			if (state.adminHoursUsageRequestId === action.meta.requestId) state.adminHoursUsage = null;
		});
		// getUserSessionAllocations
		builder.addCase(getUserSessionAllocations.pending, (state) => {
			state.loading = true;
			state.error = initialUserSessionAllocationState.error;
		});
		builder.addCase(getUserSessionAllocations.fulfilled, (state, action) => {
			state.loading = false;
			state.userSessionAllocations = action.payload;
		});
		builder.addCase(getUserSessionAllocations.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});

		// getUserSessionAllocation
		builder.addCase(getUserSessionAllocation.pending, (state) => {
			state.loading = true;
			state.error = initialUserSessionAllocationState.error;
		});
		builder.addCase(getUserSessionAllocation.fulfilled, (state, action) => {
			state.loading = false;
			// Update the allocation in the list if it exists
			const index = state.userSessionAllocations.findIndex((allocation) => allocation.id === action.payload.id);

			if (index !== -1) {
				state.userSessionAllocations[index] = action.payload;
			}
		});
		builder.addCase(getUserSessionAllocation.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});

		// createUserSessionAllocation
		builder.addCase(createUserSessionAllocation.pending, (state) => {
			state.loading = true;
			state.error = initialUserSessionAllocationState.error;
		});
		builder.addCase(createUserSessionAllocation.fulfilled, (state, action) => {
			state.loading = false;
			state.userSessionAllocations.push(action.payload);
		});
		builder.addCase(createUserSessionAllocation.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});

		// updateUserSessionAllocation
		builder.addCase(updateUserSessionAllocation.pending, (state) => {
			state.loading = true;
			state.error = initialUserSessionAllocationState.error;
		});
		builder.addCase(updateUserSessionAllocation.fulfilled, (state, action) => {
			state.loading = false;
			const index = state.userSessionAllocations.findIndex((allocation) => allocation.id === action.payload.id);

			if (index !== -1) {
				state.userSessionAllocations[index] = action.payload;
			}
		});
		builder.addCase(updateUserSessionAllocation.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});

		// deleteUserSessionAllocation
		builder.addCase(deleteUserSessionAllocation.pending, (state) => {
			state.isDeleting = true;
			state.error = initialUserSessionAllocationState.error;
		});
		builder.addCase(deleteUserSessionAllocation.fulfilled, (state, action) => {
			state.isDeleting = false;
			// Remove the deleted allocation from the list
			state.userSessionAllocations = state.userSessionAllocations.filter(
				(allocation) => allocation.id !== action.meta.arg,
			);
		});
		builder.addCase(deleteUserSessionAllocation.rejected, (state, action) => {
			state.isDeleting = false;
			state.error = action.payload as ErrorProperties;
		});

		// getUserActiveAllocations
		builder.addCase(getUserActiveAllocations.pending, (state) => {
			state.loading = true;
			state.error = initialUserSessionAllocationState.error;
		});
		builder.addCase(getUserActiveAllocations.fulfilled, (state, action) => {
			state.loading = false;
			state.userActiveAllocations = action.payload;
		});
		builder.addCase(getUserActiveAllocations.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});

		// checkUserHasAllocation
		builder.addCase(checkUserHasAllocation.pending, (state) => {
			state.loading = true;
			state.error = initialUserSessionAllocationState.error;
		});
		builder.addCase(checkUserHasAllocation.fulfilled, (state, action) => {
			state.loading = false;
			state.userHasAllocation = action.payload;
		});
		builder.addCase(checkUserHasAllocation.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});

		// getUserRemainingHours
		builder.addCase(getUserRemainingHours.pending, (state) => {
			state.loading = true;
			state.error = initialUserSessionAllocationState.error;
		});
		builder.addCase(getUserRemainingHours.fulfilled, (state, action) => {
			state.loading = false;
			state.userRemainingHours = action.payload;
		});
		builder.addCase(getUserRemainingHours.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as ErrorProperties;
		});

		// reset state
		builder.addCase(resetState, () => initialUserSessionAllocationState);
	},
});

export const { clearDiscretionarySessionState } = userSessionAllocationSlice.actions;
export default userSessionAllocationSlice.reducer;
